import { useState, useEffect, useRef, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RasterMetadata {
  base_url: string;
  type: string;        // "imagery" | "elevation"
  format: string;      // "jpeg" | "png"
  num_levels: number;
  bounds: { west: number; south: number; east: number; north: number };
  crs_wkt: string;
  geo_transform: number[];
  raster_width: number;
  raster_height: number;
  tile_width: number;
  tile_height: number;
}

type JobStatus = 'idle' | 'polling' | 'complete' | 'failed';

interface UseRasterTileSourceOptions {
  jobId?: string;
  pollInterval?: number; // ms, default 3000
}

interface UseRasterTileSourceReturn {
  registerRasterJob: (jobId: string) => Promise<number | null>;
  status: JobStatus;
  error: string | null;
  tileSourceIndex: number | null;
  stepDescription: string | null;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Standalone function (for browser console testing)
// ---------------------------------------------------------------------------

/**
 * Register a completed raster tiling job with the C++ renderer.
 * Can be called from the browser console: `window.addRasterTileSource('JOB_ID')`
 */
export async function addRasterTileSource(jobId: string): Promise<number | null> {
  const res = await fetch(`${API_BASE}/api/raster/metadata/${jobId}`);
  if (!res.ok) throw new Error(`Metadata fetch failed: ${res.status}`);
  const meta: RasterMetadata = await res.json();

  const r = (window as any)._scRenderer;
  if (!r || typeof r.addWebRaster !== 'function') {
    throw new Error('Renderer not available or addWebRaster not found');
  }

  const ext = meta.format === 'jpeg' ? '.jpg' : '.png';
  const isElevation = meta.type === 'elevation';
  const geoTransformJson = JSON.stringify(meta.geo_transform);

  const idx = r.addWebRaster(
    meta.crs_wkt,
    geoTransformJson,
    meta.raster_width,
    meta.raster_height,
    isElevation,
    meta.base_url,
    ext,
  );

  // Fly camera to the raster location
  const b = meta.bounds;
  if (b) {
    r.flyTo((b.west + b.east) / 2, (b.south + b.north) / 2, 50000);
  }

  console.log(
    `[addRasterTileSource] Registered job ${jobId} as web raster ${idx}, ` +
    `${meta.raster_width}x${meta.raster_height}, type=${meta.type}`,
  );

  return idx;
}

// Expose on window for console testing
(window as any).addRasterTileSource = addRasterTileSource;

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * useRasterTileSource -- registers a completed raster tiling job with the C++ renderer.
 *
 * Usage:
 *   const { registerRasterJob } = useRasterTileSource();
 *   // After job completes:
 *   registerRasterJob(jobId);
 *
 * Or for auto-polling:
 *   useRasterTileSource({ jobId: 'abc', pollInterval: 3000 });
 */
export function useRasterTileSource(
  options?: UseRasterTileSourceOptions,
): UseRasterTileSourceReturn {
  const [status, setStatus] = useState<JobStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [tileSourceIndex, setTileSourceIndex] = useState<number | null>(null);
  const [stepDescription, setStepDescription] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const registerRasterJob = useCallback(async (jobId: string): Promise<number | null> => {
    try {
      const idx = await addRasterTileSource(jobId);
      setTileSourceIndex(idx);
      setStatus('complete');
      setError(null);
      return idx;
    } catch (e: any) {
      const msg = e?.message || String(e);
      setError(msg);
      setStatus('failed');
      console.error('[useRasterTileSource] Registration failed:', msg);
      return null;
    }
  }, []);

  // Auto-poll mode: if jobId is provided, poll status until complete/failed
  useEffect(() => {
    const jobId = options?.jobId;
    if (!jobId) return;

    const interval = options?.pollInterval ?? 3000;
    setStatus('polling');
    setError(null);
    setStepDescription(null);

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/raster/status/${jobId}`);
        if (!res.ok) {
          setError(`Status fetch failed: ${res.status}`);
          setStatus('failed');
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
        const data = await res.json();
        setStepDescription(data.step_description || null);

        if (data.status === 'complete') {
          if (pollRef.current) clearInterval(pollRef.current);
          await registerRasterJob(jobId);
        } else if (data.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setError(data.error || 'Job failed');
          setStatus('failed');
        }
        // else: still processing, keep polling
      } catch (e: any) {
        // Network error — keep polling (transient failure)
        console.warn('[useRasterTileSource] Poll error:', e?.message);
      }
    };

    // Poll immediately, then on interval
    poll();
    pollRef.current = setInterval(poll, interval);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [options?.jobId, options?.pollInterval, registerRasterJob]);

  return { registerRasterJob, status, error, tileSourceIndex, stepDescription };
}
